import React, {
  ComponentProps,
  forwardRef,
  useCallback,
  useEffect,
} from 'react'

import { cn } from '@/lib/utils.ts'
import { Textarea } from '@/components/ui/textarea'

type TextareaProps = ComponentProps<typeof Textarea>

export interface AdaptiveTextareaProps extends Omit<TextareaProps, 'onResize'> {
  onResize?: (height: number) => void
}

export const AdaptiveTextarea = forwardRef<
  HTMLTextAreaElement,
  AdaptiveTextareaProps
>(({ onResize, ...props }, ref) => {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
      onResize?.(textarea.scrollHeight)
    }
  }, [onResize, props.value])

  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      const resizeObserver = new ResizeObserver(adjustHeight)
      resizeObserver.observe(textarea)
      return () => resizeObserver.disconnect()
    }
  }, [adjustHeight])

  return (
    <Textarea
      {...props}
      className={cn('min-h-0', props.className)}
      ref={(node) => {
        textareaRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      }}
      onChange={(e) => {
        props.onChange?.(e)
        adjustHeight()
      }}
    />
  )
})

AdaptiveTextarea.displayName = 'AdaptiveTextarea'
